import { RefObject, useEffect, useRef } from "react";
import { activityLog } from "../model/activityLog";
import { cleanValue } from "./sanitize";
import { toLocalIso } from "./time";

interface ItemState {
    id: string;
    rank: number;
    firstMs: number | null;
    visibleMs: number;
    since: number | null;
    ratio: number;
    maxRatio: number;
}

const SEEN_MS = 1_000;

/**
 * Какие элементы списка человек действительно видел: не меньше половины
 * элемента на экране не меньше секунды, пока вкладка видима. Элементы,
 * обрезанные прокручиваемым родителем, IntersectionObserver и так не считает
 * видимыми.
 */
export const useImpressions = (
    ref: RefObject<HTMLElement | null>,
    o: { list: string; itemsKey: string; ctx?: Record<string, unknown>; enabled?: boolean },
): void => {
    const ctxRef = useRef(o.ctx);
    ctxRef.current = o.ctx;
    const enabled = o.enabled !== false;
    const { list, itemsKey } = o;

    useEffect(() => {
        const root = ref.current;
        if (!enabled || !root || !activityLog.enabled()) return;
        if (typeof IntersectionObserver === "undefined") return;
        const items = new Map<Element, ItemState>();
        const served: string[] = [];
        root.querySelectorAll("[data-track-item-id]").forEach((node, i) => {
            const id = cleanValue(node.getAttribute("data-track-item-id"), 80);
            if (!id) return;
            const rank = Number(node.getAttribute("data-track-rank"));
            items.set(node, {
                id,
                rank: Number.isFinite(rank) && rank > 0 ? rank : i + 1,
                firstMs: null,
                visibleMs: 0,
                since: null,
                ratio: 0,
                maxRatio: 0,
            });
            served.push(id);
        });
        if (items.size === 0) return;

        const tabVisible = () => document.visibilityState !== "hidden";
        const openSpan = (st: ItemState, now: number) => {
            if (st.since !== null) return;
            st.since = now;
            if (st.firstMs === null) st.firstMs = now;
        };
        const closeSpan = (st: ItemState, now: number) => {
            if (st.since === null) return;
            st.visibleMs += now - st.since;
            st.since = null;
        };

        const io = new IntersectionObserver(
            (entries) => {
                const now = Date.now();
                entries.forEach((e) => {
                    const st = items.get(e.target);
                    if (!st) return;
                    st.ratio = e.intersectionRatio;
                    if (e.intersectionRatio > st.maxRatio) st.maxRatio = e.intersectionRatio;
                    if (e.isIntersecting && e.intersectionRatio >= 0.5 && tabVisible()) {
                        openSpan(st, now);
                    } else closeSpan(st, now);
                });
            },
            { root: null, threshold: [0, 0.5] },
        );
        items.forEach((_, node) => io.observe(node));

        const onVisibility = () => {
            const now = Date.now();
            items.forEach((st) => {
                if (!tabVisible()) closeSpan(st, now);
                else if (st.ratio >= 0.5) openSpan(st, now);
            });
        };
        document.addEventListener("visibilitychange", onVisibility);

        const emit = (partial: boolean) => {
            const now = Date.now();
            const seen: Array<Record<string, unknown> & { rank: number }> = [];
            items.forEach((st) => {
                const ms = st.visibleMs + (st.since !== null ? now - st.since : 0);
                if (ms < SEEN_MS || st.firstMs === null) return;
                seen.push({
                    id: st.id,
                    rank: st.rank,
                    first_seen_local: toLocalIso(new Date(st.firstMs)),
                    visible_ms: Math.round(ms),
                    max_ratio: Math.round(st.maxRatio * 100) / 100,
                });
            });
            if (partial && seen.length === 0) return;
            seen.sort((a, b) => a.rank - b.rank);
            activityLog.track("list_impressions", {
                list,
                ctx: ctxRef.current,
                served_n: served.length,
                served: served.slice(0, 100),
                seen,
                partial: partial || undefined,
            });
            if (!partial) return;
            // Снимок ушёл — дальше считаем заново, чтобы не задвоить время.
            items.forEach((st) => {
                st.visibleMs = 0;
                st.since = st.since !== null ? now : null;
                st.firstMs = st.since;
                st.maxRatio = st.ratio;
            });
        };
        const unsubscribe = activityLog.onBeforeFinal(() => emit(true));

        return () => {
            io.disconnect();
            document.removeEventListener("visibilitychange", onVisibility);
            unsubscribe();
            emit(false);
        };
    }, [ref, enabled, list, itemsKey]);
};
